import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  DocumentThumbnail,
  DocumentViewer,
  MiCard,
  MiChip,
  MiIcon,
  MiModalConfirm,
} from "@miplatform/components";
import {
  ATTEMPT_STATUSES,
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  DATE_TIME_FORMATS,
  TYPOGRAPHY_VARIANTS,
  VerificationRequestPolicy,
} from "@miplatform/constants";
import { useFeatureFlags } from "@miplatform/feature-flags";
import {
  getContrastColor,
  getRootUrl,
  toFormattedDate,
} from "@miplatform/utils";
import {
  mdiChevronDown,
  mdiChevronUp,
  mdiThumbDown,
  mdiThumbUp,
} from "@mdi/js";
import { Footer } from "@mibuildweb/components";
import { Box, Typography } from "@mui/material";
import { IssueCard } from "../../../IssueCard/IssueCard";

const REVIEW_DOCUMENTS = {
  CUSTOMER: "CUSTOMER_SIGNATURE",
  TECHNICIAN: "TECHNICIAN_SIGNATURE",
  ATTEMPT_SUBMISSION: "ATTEMPT_SUBMISSION",
};

const getReviewDocument = (imageType, documents = []) =>
  documents.find((document) => {
    try {
      return JSON.parse(document.externalData || "{}").imageType === imageType;
    } catch {
      return false;
    }
  });

const formatSurveyAnswer = (answer) => {
  const value = String(answer ?? "");
  return value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : "--";
};

const ReviewSection = ({ title, children }) => {
  const { classes } = useStyles();

  return (
    <Box className={classes.reviewSection}>
      <Box className={classes.reviewSectionHeader}>
        <Typography
          className={classes.fieldsets}
          variant={TYPOGRAPHY_VARIANTS.HD2}
        >
          {title}
        </Typography>
      </Box>
      <Box className={classes.reviewSectionContent}>{children}</Box>
    </Box>
  );
};

const StatusCards = ({ assignment = {}, issues = [] }) => {
  const {
    order: { orderTemplateColor, orderTemplateAbbreviation } = {},
    assignmentNumber,
  } = assignment;

  const { classes } = useStyles({ orderTemplateColor });
  const [isAccordionOpen, setIsAccordionOpen] = useState(true);

  return (
    <Box>
      <MiCard
        className={classes.statusCard}
        title={`Current Order: #${assignmentNumber}`}
        customLeftIcon={
          <MiChip
            className={classes.chip}
            label={orderTemplateAbbreviation}
            size="small"
          />
        }
        onRightIconClick={() => setIsAccordionOpen((prev) => !prev)}
        rightIcon={isAccordionOpen ? mdiChevronUp : mdiChevronDown}
      >
        {isAccordionOpen && (
          <Box className={classes.cardsContainer}>
            {issues.map((issue, i) => (
              <IssueCard key={issue.id} issue={issue} issueIndex={i + 1} />
            ))}
          </Box>
        )}
      </MiCard>
    </Box>
  );
};

const MiDayReviewSection = ({ attempt }) => {
  const [showImagesViewer, setShowImagesViewer] = useState(false);
  const handlePreview = () => {
    setShowImagesViewer(true);
  };
  const surveyData = attempt?.surveyData || {};
  const customerSignature = getReviewDocument(
    REVIEW_DOCUMENTS.CUSTOMER,
    attempt?.documents
  );
  const technicianSignature = getReviewDocument(
    REVIEW_DOCUMENTS.TECHNICIAN,
    attempt?.documents
  );
  const technicianSubmission = getReviewDocument(
    REVIEW_DOCUMENTS.ATTEMPT_SUBMISSION,
    attempt?.documents
  );
  const { theme, classes } = useStyles({
    hasTechnicianSubmission: Boolean(technicianSubmission),
  });
  const customerInfo = JSON.parse(customerSignature?.externalData || "{}");

  const technicianInfo = JSON.parse(technicianSignature?.externalData || "{}");

  return (
    <>
      {customerSignature && (
        <ReviewSection
          title={`Homeowner / Representative : ${
            surveyData.respondentName ?? ""
          }`}
        >
          <Box className={classes.certificationRow}>
            <Typography variant={TYPOGRAPHY_VARIANTS.BD2}>
              I certify that the services have been completed to my
              satisfaction:
            </Typography>
            <MiChip
              label={surveyData.satisfied ? "I Agree" : "I Disagree"}
              variant="filled"
              className={classes.agreeChip}
              icon={
                <MiIcon
                  path={surveyData.satisfied ? mdiThumbUp : mdiThumbDown}
                  color={theme.palette.common.white}
                  size={0.4}
                  className={classes.agreementIcon}
                />
              }
              size="medium"
              color={surveyData.satisfied ? "success" : "error"}
            />
          </Box>
          <SignatureComponent
            signatureDocument={customerSignature}
            name={customerInfo?.customerName}
            signedAt={toFormattedDate(
              customerInfo?.signedAt,
              DATE_TIME_FORMATS.FORMAT_MMM_D_YYYY_H_MM_A
            )}
          />

          {surveyData.response && (
            <Box className={classes.sectionBlock}>
              <Typography
                variant={TYPOGRAPHY_VARIANTS.HD2}
                className={classes.sectionTitle}
              >
                Survey Results
              </Typography>
              {surveyData.response.responses?.map((response, index) => (
                <Box
                  className={classes.responseRow}
                  key={response.question + index}
                >
                  <Typography variant={TYPOGRAPHY_VARIANTS.BD2}>
                    {response.question}:{" "}
                  </Typography>
                  <Typography
                    variant={TYPOGRAPHY_VARIANTS.BD2}
                    fontWeight={theme.typography.fontWeightMedium}
                  >
                    {formatSurveyAnswer(response.answer)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          <CommentSection
            title="Customer Comments"
            comment={surveyData.comment}
          />
        </ReviewSection>
      )}

      <ReviewSection title="Technician Signature">
        <Box>
          <Box className={classes.technicianSignatureRow}>
            <SignatureComponent
              signatureDocument={technicianSignature}
              name={technicianInfo?.technicianName}
              signedAt={toFormattedDate(
                technicianInfo?.signedAt,
                DATE_TIME_FORMATS.FORMAT_MMM_D_YYYY_H_MM_A
              )}
            />
            <DocumentThumbnail
              document={technicianSubmission}
              documentCount={1}
              dimension={200}
              aspectRatio={7 / 5}
              onClick={handlePreview}
              className={classes.technicianSubmission}
            />
          </Box>
          <CommentSection
            title="Technician Comments"
            comment={attempt?.technicianComment ?? ""}
          />
        </Box>

        <DocumentViewer
          documents={[technicianSubmission]}
          onUnfullscreen={() => {
            setShowImagesViewer(false);
          }}
          fullScreenStatus={showImagesViewer}
        />
      </ReviewSection>
    </>
  );
};

const SignatureComponent = ({ signatureDocument, name, signedAt }) => {
  const API_URL = getRootUrl();
  const { classes } = useStyles();

  return (
    <Box>
      {signatureDocument && (
        <img
          src={`${API_URL}/${signatureDocument.referenceId}`}
          alt="Signature"
          className={classes.signatureImage}
        />
      )}
      <Typography
        variant={TYPOGRAPHY_VARIANTS.BD2}
        className={classes.signatureMetadata}
      >
        {"Signed by: "}
        <span className={classes.signatureName}>{name}</span> @ {signedAt}
      </Typography>
    </Box>
  );
};

const CommentSection = ({ title, comment }) => {
  const { classes } = useStyles();
  return (
    <Box className={classes.sectionBlock}>
      <Typography
        variant={TYPOGRAPHY_VARIANTS.HD2}
        className={classes.sectionTitle}
      >
        {title}
      </Typography>
      <Box className={classes.commentContent}>
        <Typography variant={TYPOGRAPHY_VARIANTS.BD2}>
          {comment || "_"}
        </Typography>
      </Box>
    </Box>
  );
};

export const Review = ({
  attempt,
  onCancel,
  onEmailDrawerOpen,
  verificationReview = false,
  requestVerification = async () => false,
  isLoading = false,
  completeVerification = async () => false,
}) => {
  const { classes } = useStyles();

  const [modalConfig, setModalConfig] = useState(null);
  const { featureFlags } = useFeatureFlags();
  const isMiDayFeatureEnabled = featureFlags?.miDayEnabled;
  const showEmailAction = [
    ATTEMPT_STATUSES.CLOSEOUT_IN_PROGRESS,
    ATTEMPT_STATUSES.SUBMITTED,
  ].includes(attempt?.attemptStatus);

  const canRequestVerification =
    attempt?.assignment?.order?.verificationRequestPolicy !==
      VerificationRequestPolicy.CannotRequestVerification &&
    !attempt?.pendingVerification &&
    !verificationReview &&
    !attempt?.verifiedBy;

  const handleRequestVerification = async () => {
    const isRequested = await requestVerification();

    if (isRequested) {
      setModalConfig(null);
      onCancel();
    }
  };

  const actions = [
    {
      name: "Cancel",
      variant: BUTTON_VARIANTS.TEXT,
      color: BUTTON_COLORS.BLACK,
      onClick: onCancel,
    },
  ];

  if (canRequestVerification) {
    actions.push({
      name: "Request Verification",
      variant: BUTTON_VARIANTS.OUTLINED,
      color: BUTTON_COLORS.ORANGE,
      onClick: () => {
        setModalConfig({
          title: "Request Verification Review",
          description: "Would you like to send this for verification review?",
          textPositive: "Send for review",
          handlePositive: handleRequestVerification,
          handleNegative: () => setModalConfig(null),
        });
      },
    });
  }

  if (verificationReview && !attempt?.verifiedBy) {
    actions.push({
      name: "Complete Review",
      variant: BUTTON_VARIANTS.OUTLINED,
      onClick: async () => {
        setModalConfig({
          title: "Complete Review",
          description: "Are you sure you want to complete the review?",
          textPositive: "Complete",
          handlePositive: async () => {
            const isUpdated = await completeVerification();

            if (isUpdated) {
              setModalConfig(null);
              onCancel();
            }
          },
          handleNegative: () => setModalConfig(null),
        });
      },
    });
  }

  if (showEmailAction && !verificationReview) {
    actions.push({
      name: "Start Email",
      variant: BUTTON_VARIANTS.CONTAINED,
      onClick: async () => {
        onEmailDrawerOpen();
      },
    });
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.issueContainer}>
        <StatusCards assignment={attempt.assignment} issues={attempt.issues} />
        {isMiDayFeatureEnabled && <MiDayReviewSection attempt={attempt} />}
      </Box>

      <Footer sticky show actions={actions} />
      <MiModalConfirm
        open={!!modalConfig}
        title={modalConfig?.title}
        textPositive={modalConfig?.textPositive}
        description={modalConfig?.description}
        loading={isLoading}
        handlePositive={modalConfig?.handlePositive}
        handleNegative={modalConfig?.handleNegative}
      />
    </Box>
  );
};

const useStyles = makeStyles()(
  (theme, { hasTechnicianSubmission = false, orderTemplateColor } = {}) => ({
    root: {
      height: "100%",
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
    },
    cardsContainer: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
    },
    issueContainer: {
      minHeight: 0,
      overflowY: "auto",
      flex: 1,
    },
    fieldsets: {
      fontWeight: theme.fontWeight.semiBold,
    },
    reviewSection: {
      overflow: "hidden",
      border: `1px solid ${theme.palette.grey.lightAccent}`,
      borderRadius: theme.spacing(0.5),
      marginTop: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
    },
    reviewSectionHeader: {
      padding: theme.spacing(1.5, 2),
      backgroundColor: theme.palette.grey.lightAccent,
      borderBottom: `1px solid ${theme.palette.grey.lightAccent}`,
    },
    reviewSectionContent: {
      padding: theme.spacing(4),
    },
    certificationRow: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: theme.spacing(1),
    },
    signatureImage: {
      display: "block",
      width: 200,
      marginTop: theme.spacing(2),
    },
    chip: {
      borderRadius: theme.spacing(0.5),
      minWidth: 18,
      height: 18,
      fontSize: theme.fontSize.xxsmall,
      fontWeight: theme.fontWeight.semiBold,
      ...(orderTemplateColor && {
        backgroundColor: orderTemplateColor,
        color: getContrastColor(orderTemplateColor),
      }),
      "& .MuiChip-label": {
        padding: 0,
      },
    },
    statusCard: {
      "&&": {
        padding: theme.spacing(2),
        margin: 0,
        marginTop: theme.spacing(2),
        border: `1px solid ${theme.palette.green.main}`,
        borderRadius: theme.spacing(1),
      },
    },
    agreementIcon: {
      marginRight: `${theme.spacing(0.2)} !important`,
    },
    technicianSignatureRow: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
    },
    technicianSubmission: {
      "&&": {
        borderRadius: 3,
        border: hasTechnicianSubmission
          ? "none"
          : `1px solid ${theme.palette.grey.main}`,
      },
    },
    signatureMetadata: {
      alignSelf: "flex-end",
      color: theme.palette.grey.darkerAccent,
    },
    signatureName: {
      fontWeight: "bold",
    },
    sectionTitle: {
      fontWeight: theme.fontWeight.semiBold,
    },
    sectionBlock: {
      marginTop: theme.spacing(6),
    },
    responseRow: {
      display: "flex",
      flexDirection: "row",
      gap: theme.spacing(3),
      marginTop: theme.spacing(4),
      marginLeft: theme.spacing(4),
    },
    commentContent: {
      marginTop: theme.spacing(2),
      marginLeft: theme.spacing(4),
    },
    agreeChip: {
      marginLeft: theme.spacing(1),
      height: 20,
      minWidth: 60,
      borderRadius: theme.spacing(0.5),
      fontSize: theme.fontSize.xsmall,
      "& .MuiChip-label": {
        padding: theme.spacing(0, 1),
      },
      "& .MuiChip-icon": {
        marginLeft: theme.spacing(1),
      },
    },
  })
);
